import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { APIError, isQuotaError, notifyCreditsChanged, type Course } from "@/api/client";
import CreditCost from "@/components/CreditCost";
import Header from "@/components/Layout/Header";
import LoadingOverlay from "@/components/Loading";
import QuotaExhaustedCard from "@/components/QuotaExhaustedCard";
import StepHeader from "@/components/StepHeader";

const ACCEPTED = ".pdf,.docx,.txt,.md";
const MAX_BYTES = 32 * 1024 * 1024;

// Eigener fetch statt api-Client, weil hier FormData (Datei) geht.
async function postExtract(form: FormData): Promise<Course> {
  const token = localStorage.getItem("ca_access_token");
  const res = await fetch("/ca/api/v1/courses/extract", {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) {
    let data: unknown;
    try {
      data = (await res.json()).detail;
    } catch {
      // ignore
    }
    if (res.status === 402) notifyCreditsChanged();
    throw new APIError(
      res.status,
      typeof data === "string" ? data : `HTTP ${res.status}`,
      data,
    );
  }
  const course = (await res.json()) as Course;
  notifyCreditsChanged(); // Extraktion kostet Credits — Balance im Header neu
  return course;
}

export default function Extract() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mode, setMode] = useState<"upload" | "text">("upload");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quota, setQuota] = useState<unknown>(null);

  // Gemeinsame Fehlerbehandlung: 402 → Quota-Karte, Rest → Fehlertext.
  const handleError = (err: unknown) => {
    if (isQuotaError(err)) setQuota(err.data);
    else setError(err instanceof Error ? err.message : t("extract.failed"));
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setError(t("extract.fileTooLarge", { size: (file.size / 1024 / 1024).toFixed(1) }));
      return;
    }
    setError(null);
    setQuota(null);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const course = await postExtract(fd);
      navigate(`/courses/${course.id}/workspace`);
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
      // Reset, sonst feuert onChange beim selben File nicht nochmal
      e.target.value = "";
    }
  };

  const onTextSubmit = async () => {
    if (!text.trim()) return;
    setError(null);
    setQuota(null);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("text", text);
      const course = await postExtract(fd);
      navigate(`/courses/${course.id}/workspace`);
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Header />
      {busy && <LoadingOverlay message={t("extract.busy")} />}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-ca-card rounded-xl border border-ca-surface-border p-8 md:p-14 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-ca-accent to-transparent opacity-30" />

          <StepHeader
            step={1}
            title={t("extract.stepTitle")}
            desc={t("extract.stepDesc")}
          />

          <div className="flex bg-ca-card p-2 rounded-2xl mb-10 border border-ca-surface-border">
            <button
              onClick={() => setMode("upload")}
              className={`flex-1 py-4 text-xs font-black uppercase tracking-widest rounded-xl transition-all ${
                mode === "upload"
                  ? "bg-ca-accent text-ca-brand shadow-lg"
                  : "text-ca-surface-muted hover:text-ca-surface-ink"
              }`}
            >
              {t("extract.modeUpload")}
            </button>
            <button
              onClick={() => setMode("text")}
              className={`flex-1 py-4 text-xs font-black uppercase tracking-widest rounded-xl transition-all ${
                mode === "text"
                  ? "bg-ca-accent text-ca-brand shadow-lg"
                  : "text-ca-surface-muted hover:text-ca-surface-ink"
              }`}
            >
              {t("extract.modeText")}
            </button>
          </div>

          {mode === "upload" ? (
            <div className="mb-10 p-12 border-2 border-dashed border-ca-surface-border rounded-xl bg-ca-card flex flex-col items-center justify-center text-center group hover:border-ca-accent/50 transition-colors">
              <label className="cursor-pointer">
                <input
                  type="file"
                  className="hidden"
                  accept={ACCEPTED}
                  onChange={onFile}
                  disabled={busy}
                />
                <div className="px-12 py-5 bg-ca-accent text-ca-brand font-black uppercase tracking-widest rounded-2xl shadow-xl hover:scale-105 transition active:scale-95">
                  {t("extract.uploadButton")}
                </div>
              </label>
              <p className="mt-6 text-xs text-ca-surface-muted font-bold uppercase tracking-tighter">
                {t("extract.uploadHint")}
              </p>
              <p className="mt-2 text-[10px] text-ca-surface-muted italic">
                {t("extract.uploadVision")}
              </p>
              <p className="mt-2 text-xs">
                <CreditCost feature="extract" />
              </p>
            </div>
          ) : (
            <div className="mb-10">
              <textarea
                className="w-full px-8 py-6 rounded-xl bg-white border border-ca-surface-border outline-none focus:border-ca-accent transition-all text-ca-surface-ink text-base leading-relaxed placeholder:text-ca-surface-muted"
                placeholder={t("extract.textPlaceholder")}
                rows={10}
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={busy}
              />
              <button
                onClick={onTextSubmit}
                disabled={!text.trim() || busy}
                className="w-full mt-6 py-5 bg-ca-accent text-ca-brand font-black uppercase tracking-widest rounded-2xl shadow-xl hover:scale-[1.02] disabled:opacity-30 disabled:grayscale transition-all active:scale-95"
              >
                {t("extract.analyzeButton")}
              </button>
              <p className="mt-3 text-center text-xs">
                <CreditCost feature="extract" />
              </p>
            </div>
          )}

          {quota !== null && (
            <div className="mb-6">
              <QuotaExhaustedCard detail={quota} onDismiss={() => setQuota(null)} />
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-verdict-red-bg border border-verdict-red/40 rounded-2xl text-verdict-red text-sm">
              <strong className="font-black uppercase tracking-widest text-xs block mb-1">
                {t("common.error")}
              </strong>
              {error}
            </div>
          )}

          <div className="pt-10 border-t border-ca-surface-border space-y-3">
            <p className="text-[10px] font-black uppercase text-ca-surface-muted tracking-[0.3em] text-center">
              {t("extract.orManual")}
            </p>
            <button
              onClick={() => navigate("/")}
              className="w-full py-4 bg-ca-card border border-ca-surface-border text-ca-surface-ink font-black uppercase tracking-widest rounded-2xl hover:bg-ca-card transition active:scale-95"
            >
              {t("extract.emptyCourse")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
